/**
 * @file Holds a level's visibility keys and owns the two RESIDENT channels.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * KEYS ARE SOLVED PER CHUNK, AFTER CHUNKING — AND THAT IS NOT AN OPTIMISATION
 * ─────────────────────────────────────────────────────────────────────────────
 * The obvious order is to key the merged geometry and let `world/Chunker.js`
 * slice the result like it slices `color`/`color1`. That does not work: the
 * chunker carries only the attributes present at the time, and emits no
 * source-vertex map, so once a chunk exists there is no way to write a
 * DIFFERENT key into it. Pre-chunk keys can be baked, but never swapped — which
 * is the entire feature.
 *
 * So the store is built from the CHUNKS. Each chunk owns its own `KEY_COUNT`
 * channels, and the hourly swap is a local buffer write with no indirection.
 * The price is that the dedup can no longer collapse vertices across a chunk
 * boundary; at `CHUNK_DIV` 8 that is a rounding error against the win.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ THE FIRST KEY MUST LAND BEFORE THE FIRST RENDER
 * ─────────────────────────────────────────────────────────────────────────────
 * A material built with `{sun: true}` reads `color3`. If that attribute does not
 * exist when the mesh is first drawn, the pipeline is built against a missing
 * attribute — and adding it later does not rebuild the pipeline. `apply()` must
 * therefore be called once during the level build, with whatever single key is
 * available, BEFORE the level is handed to the renderer. Everything after that
 * is a contents rewrite (`needsUpdate`) and costs no pipeline work.
 *
 * That constraint is also what makes progressive streaming safe: bake one key
 * synchronously, apply it to both resident slots, ship the level, and let the
 * rest arrive.
 *
 * @module engine/bake/SunKeyStore
 */

import { applySunVis } from './sunKeys.js';

/** @typedef {{geometry: any}} KeyTarget A mesh, or anything with `.geometry`. */

export class SunKeyStore {
  /**
   * @param {KeyTarget[]} targets The chunk meshes. Order is fixed for the
   *   store's lifetime — {@link setKey} is indexed by it.
   * @param {number} keyCount Usually `sunArc.KEY_COUNT`.
   */
  constructor(targets, keyCount) {
    /** @type {KeyTarget[]} */
    this.targets = targets;
    /** @type {number} */
    this.keyCount = keyCount;
    /**
     * `keys[k][t]` is the `Uint8Array` for key `k`, target `t`. A key slot stays
     * null until every target has been filled for it — a half-filled key would
     * light some chunks by one sun and the rest by another.
     * @type {Array<Uint8Array[]|null>}
     */
    this.keys = new Array(keyCount).fill(null);
    /** @type {number} How many keys have arrived. */
    this.ready = 0;
    /** Requested pair (what the clock asked for). */
    this._a = -1;
    this._b = -1;
    /** RESOLVED pair actually on the GPU — differs from the above while keys
     *  are still streaming and `nearestReady` is standing in. */
    this._ra = -1;
    this._rb = -1;
  }

  /**
   * @param {number} k
   * @param {Uint8Array[]} arrays One per target, in constructor order.
   */
  setKey(k, arrays) {
    if (k < 0 || k >= this.keyCount) throw new RangeError(`setKey: key ${k} out of range`);
    if (arrays.length !== this.targets.length) {
      throw new Error(`setKey: expected ${this.targets.length} arrays, got ${arrays.length}`);
    }
    if (!this.keys[k]) this.ready++;
    this.keys[k] = arrays;
    // A newly-arrived key may be a better match for what is currently on the
    // GPU than the one standing in for it, so re-resolve.
    if (this._a >= 0) this.apply(this._a, this._b, true);
  }

  /** @param {number} k @returns {boolean} */
  has(k) {
    return !!this.keys[k];
  }

  /**
   * The ready key closest to `k`. While the keys are still streaming this is
   * what stands in for one that has not arrived — the sun's shading LOCKS to
   * the nearest baked direction instead of going white or black.
   *
   * @param {number} k
   * @returns {number} -1 if nothing is ready at all.
   */
  nearestReady(k) {
    if (this.keys[k]) return k;
    for (let d = 1; d < this.keyCount; d++) {
      if (k - d >= 0 && this.keys[k - d]) return k - d;
      if (k + d < this.keyCount && this.keys[k + d]) return k + d;
    }
    return -1;
  }

  /**
   * Uploads the pair straddling the current hour. Cheap and idempotent: it
   * returns immediately when the resolved pair is already resident.
   *
   * @param {number} a
   * @param {number} b
   * @param {boolean} [force] Re-upload even if the pair is unchanged.
   * @returns {boolean} Whether anything was written.
   */
  apply(a, b, force = false) {
    const ra = this.nearestReady(a);
    const rb = this.nearestReady(b);
    if (ra < 0 || rb < 0) return false;

    this._a = a;
    this._b = b;
    if (!force && ra === this._ra && rb === this._rb) return false;
    this._ra = ra;
    this._rb = rb;

    const ka = this.keys[ra];
    const kb = this.keys[rb];
    for (let t = 0; t < this.targets.length; t++) {
      applySunVis(this.targets[t].geometry, ka[t], kb[t]);
    }
    return true;
  }

  /** Drops the stored channels. The geometries keep their resident `color3`. */
  dispose() {
    this.keys.fill(null);
    this.targets = [];
    this.ready = 0;
  }
}
